import { OperationalNotifications } from "./OperationalNotifications";
import { useEffect, useState } from "react";
import { ArrowLeft, Bell, CheckCheck, Gift, Settings2, Trophy } from "lucide-react";
import {
  getGamificacaoNotificationPreferences,
  getGamificacaoNotifications,
  getPushClientState,
  markAllGamificacaoNotificationsRead,
  markGamificacaoNotificationRead,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  updateGamificacaoNotificationPreferences,
  type GamificacaoInbox,
  type GamificacaoNotificationPreferences,
} from "./notificationClient";

export function NotificationsScreen({
  vapidPublicKey,
  serverConfigured,
  onBack,
  onOpenRewards,
  onUnreadChange,
  onOperationalUnreadChange,
}: {
  vapidPublicKey?: string | null;
  serverConfigured: boolean;
  onBack: () => void;
  onOpenRewards: () => void;
  onUnreadChange: (count: number) => void;
  onOperationalUnreadChange: (count: number) => void;
}) {
  const [data, setData] = useState<GamificacaoInbox>({ naoLidas: 0, itens: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [prefs, setPrefs] = useState<GamificacaoNotificationPreferences | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pushState, setPushState] = useState<{supported:boolean;permission:string;subscribed:boolean}|null>(null);
  const [pushMessage,setPushMessage] = useState("");

  useEffect(() => {
    void Promise.all([getGamificacaoNotifications(), getGamificacaoNotificationPreferences(), getPushClientState()])
      .then(([inbox, preferences, push]) => {
        setData(inbox);
        setPrefs(preferences);
        setPushState(push);
        onUnreadChange(inbox.naoLidas);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Não foi possível carregar as notificações."))
      .finally(() => setLoading(false));
  }, [onUnreadChange]);

  async function markRead(id: string, url?: string | null) {
    await markGamificacaoNotificationRead(id);
    setData((current) => {
      const item = current.itens.find((x) => x.id === id);
      const decrement = item && !item.lida ? 1 : 0;
      const next = {
        naoLidas: Math.max(0, current.naoLidas - decrement),
        itens: current.itens.map((x) => (x.id === id ? { ...x, lida: true } : x)),
      };
      onUnreadChange(next.naoLidas);
      return next;
    });
    if (url?.includes("ranking")) return;
  }

  async function markAll() {
    await markAllGamificacaoNotificationsRead();
    setData((current) => ({ naoLidas: 0, itens: current.itens.map((x) => ({ ...x, lida: true })) }));
    onUnreadChange(0);
  }

  async function enablePush() {
    setPushMessage("");
    if (!serverConfigured || !vapidPublicKey) {
      setPushMessage("Os avisos neste dispositivo ainda não estão disponíveis.");
      return;
    }
    try {const permission = await requestNotificationPermission();
      if (permission !== "granted") {setPushMessage(permission === "denied" ? "Permita notificações nas configurações do navegador para receber avisos." : "Não foi possível ativar avisos neste dispositivo.");return;}
      if ("serviceWorker" in navigator) await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      await subscribeToPush(vapidPublicKey);
      await subscribeToPush(vapidPublicKey, "/api/app/notificacoes/push");
      setPushState(await getPushClientState());setPushMessage("Avisos ativados neste dispositivo, inclusive em segundo plano.");}
    catch {setPushMessage("Não foi possível ativar avisos agora. Tente novamente.");}
  }

  async function disablePush() {
    try {await unsubscribeFromPush();setPushState(await getPushClientState());setPushMessage("Avisos desativados neste dispositivo.");}
    catch {setPushMessage("Não foi possível desativar avisos agora. Tente novamente.");}
  }

  return <main className="app-lifestyle min-h-screen px-5 pb-10 pt-6 text-white">
    <div className="mx-auto max-w-lg">
      <div className="flex items-center justify-between gap-3">
        <button onClick={onBack} className="glass-icon grid h-11 w-11 place-items-center rounded-full" aria-label="Voltar"><ArrowLeft className="h-5 w-5"/></button>
        <div className="min-w-0 flex-1"><p className="text-xs font-semibold uppercase tracking-[.16em] text-teal-light">YamarHub</p><h1 className="font-display text-2xl font-semibold">Notificações</h1></div>
        <button onClick={() => setSettingsOpen((x) => !x)} className="glass-icon grid h-11 w-11 place-items-center rounded-full" aria-label="Preferências"><Settings2 className="h-5 w-5"/></button>
      </div>

      <OperationalNotifications onUnreadChange={onOperationalUnreadChange} />

      {settingsOpen && <section className="deep-ocean-card mt-5 rounded-3xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="font-display text-lg font-semibold">Preferências</h2><p className="mt-1 text-xs text-white/55">Escolha como deseja receber avisos.</p></div>{pushState?.subscribed?<button onClick={()=>void disablePush()} className="min-h-11 rounded-xl border border-white/15 px-3 py-2 text-xs font-semibold text-white/80">Desativar avisos neste celular</button>:<button onClick={()=>void enablePush()} disabled={!pushState?.supported||!serverConfigured} className="min-h-11 rounded-xl border border-teal/40 px-3 py-2 text-xs font-semibold text-teal-light disabled:opacity-40">Receber avisos neste celular</button>}</div>
        {pushMessage&&<p role="status" className="mt-3 rounded-xl border border-teal/20 bg-teal/5 p-3 text-sm text-white/80">{pushMessage}</p>}
        <div className="mt-3 grid grid-cols-3 gap-2 text-center text-[10px]"><div className="rounded-xl border border-white/8 bg-black/15 p-2"><span className="block text-white/50">Serviço</span><strong className={serverConfigured?"text-teal-light":"text-sand"}>{serverConfigured?"Disponível":"Indisponível"}</strong></div><div className="rounded-xl border border-white/8 bg-black/15 p-2"><span className="block text-white/50">Permissão</span><strong className="text-white/70">{pushState?.permission==="granted"?"Permitida":pushState?.permission==="denied"?"Bloqueada":"Não definida"}</strong></div><div className="rounded-xl border border-white/8 bg-black/15 p-2"><span className="block text-white/50">Celular</span><strong className={pushState?.subscribed?"text-teal-light":"text-white/60"}>{pushState?.subscribed?"Avisos ativos":"Avisos desligados"}</strong></div></div>
        {prefs && <div className="mt-4 grid gap-3">{([
          ["conquistaDesbloqueada", "Conquistas desbloqueadas"],
          ["desafioQuaseConcluido", "Desafios quase concluídos"],
          ["temporadaEncerrando", "Temporada encerrando"],
          ["recompensaDisponivel", "Recompensas disponíveis"],
        ] as const).map(([key, label]) => <label key={key} className="flex items-center justify-between gap-4 rounded-2xl border border-white/8 bg-white/[.03] px-4 py-3 text-sm"><span>{label}</span><input type="checkbox" checked={prefs[key]} onChange={(e) => { const next = { ...prefs, [key]: e.target.checked }; setPrefs(next); void updateGamificacaoNotificationPreferences(next); }}/></label>)}</div>}
      </section>}

      <div className="mt-6 flex items-center justify-between gap-3">
        <div><p className="text-sm font-semibold">Gamificação</p><p className="text-xs text-white/45">{data.naoLidas} não lida{data.naoLidas === 1 ? "" : "s"}</p></div>
        <div className="flex gap-2"><button onClick={onOpenRewards} className="rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-white/75"><Gift className="mr-1 inline h-3.5 w-3.5"/>Recompensas</button>{data.naoLidas > 0 && <button onClick={markAll} className="rounded-full border border-teal/30 px-3 py-2 text-xs font-semibold text-teal-light"><CheckCheck className="mr-1 inline h-3.5 w-3.5"/>Ler todas</button>}</div>
      </div>

      {loading && <p className="mt-8 text-sm text-white/50">Carregando...</p>}
      {error && <p className="mt-8 rounded-2xl border border-red-400/20 bg-red-400/10 p-4 text-sm text-red-100">{error}</p>}
      {!loading && !error && data.itens.length === 0 && <div className="deep-ocean-card mt-5 rounded-3xl p-7 text-center"><Bell className="mx-auto h-8 w-8 text-teal"/><h2 className="mt-3 font-display text-lg font-semibold">Tudo tranquilo por aqui</h2><p className="mt-1 text-sm text-white/50">Suas conquistas, desafios e recompensas aparecerão aqui.</p></div>}

      <div className="mt-4 grid gap-3">{data.itens.map((item) => <button key={item.id} onClick={() => void markRead(item.id, item.url)} className={`deep-ocean-card w-full rounded-3xl p-4 text-left ${item.lida ? "opacity-70" : "ring-1 ring-teal/35"}`}>
        <div className="flex gap-3"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-teal/10 text-teal">{item.tipo === "RecompensaDisponivel" ? <Gift className="h-5 w-5"/> : <Trophy className="h-5 w-5"/>}</span><div className="min-w-0 flex-1"><div className="flex items-start justify-between gap-2"><h3 className="font-display text-[15px] font-semibold">{item.titulo}</h3>{!item.lida && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-teal"/>}</div><p className="mt-1 text-sm leading-5 text-white/58">{item.mensagem}</p><p className="mt-2 text-[11px] text-white/35">{new Date(item.criadaEmUtc).toLocaleString("pt-BR")}</p></div></div>
      </button>)}</div>
    </div>
  </main>;
}
